import React from 'react';
import { FieldGroup, Grid, MultiSelect, Select, Stack, ToggleGroup } from 'library/ui';

import { singleChoice, multiChoice, booleanChoice } from '../utils/choices';
import { updateScale } from '../utils/scale';
import { useRequirementsAndPayContext } from '../context';

import type { IManagedScale, GenericChoice, Qualification } from '../types';

export function AdditionalQualificationsSection({ scale }: { scale: IManagedScale }) {
    const {
        qualifications: { single, multi, boolean },
        updateScales,
    } = useRequirementsAndPayContext();

    function update(
        value: number | number[],
        choices: GenericChoice[],
        qualification: Qualification | null,
        category: 'education' | 'experience' | 'multiple' | 'single' | 'boolean',
    ) {
        const newScale = updateScale(scale, value, choices, qualification, category);

        updateScales(newScale);
    }

    return (
        <Stack gap="md">
            <Grid minColumnWidth={240} gap="md" align="start">
                {multi?.map((item: { qualification: Qualification; choices: GenericChoice[] }) => (
                    <FieldGroup key={item.qualification.id} label={item.qualification.name}>
                        <MultiSelect
                            fullWidth
                            options={item.choices.map((choice) => ({ key: String(choice.key), value: choice.value }))}
                            value={(multiChoice(scale, item.qualification, item.choices) ?? []).map(String)}
                            onChange={(keys) => update(keys.map(Number), item.choices, item.qualification, 'multiple')}
                        />
                    </FieldGroup>
                ))}
                {single?.map((item: { qualification: Qualification; choices: GenericChoice[] }) => (
                    <FieldGroup key={item.qualification.id} label={item.qualification.name}>
                        <Select
                            fullWidth
                            options={item.choices.map((choice) => ({
                                key: choice.key,
                                value: String(choice.key),
                                label: choice.value,
                            }))}
                            value={String(singleChoice(scale, item.qualification, item.choices) ?? '')}
                            onChange={(value) => update(Number(value), item.choices, item.qualification, 'single')}
                        />
                    </FieldGroup>
                ))}
            </Grid>
            {boolean && boolean.length > 0 && (
                <ToggleGroup
                    multi
                    options={boolean.map((bool) => ({ label: bool.value, value: String(bool.key) }))}
                    value={boolean.filter((bool) => booleanChoice(scale, bool)).map((bool) => String(bool.key))}
                    onChange={(newValues) => {
                        const current = boolean
                            .filter((bool) => booleanChoice(scale, bool))
                            .map((bool) => String(bool.key));
                        const changedKey =
                            newValues.find((v) => !current.includes(v)) ?? current.find((v) => !newValues.includes(v));
                        const changed = boolean.find((bool) => String(bool.key) === changedKey);
                        if (changed) update(changed.key, [changed], null, 'boolean');
                    }}
                />
            )}
        </Stack>
    );
}
